import { useEffect, useState } from "react";
import { Ellipsis } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, dropdownMenuItemDestructiveClass } from "components/ui/dropdown-menu";
import { ConversationMenuIcon } from "components/blocks/layout/conversation-menu-icon";
import { CustomDialog } from "components/blocks/custom-dialog";
import { DebouncedInput } from "components/blocks/debounced-input/debounced-input";
import { useProjectActions } from "../hooks/use-project-actions";
import { useProjects } from "../hooks/use-projects";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useChatStore } from "../../../state/store/chat/use-chat-store";
import { cn } from "lib/utils";
import { DEFAULT_PROJECT_COLOR, ProjectColorSwatches } from "./project-color-swatches";

type ProjectDialog = {
    type: "rename" | "delete";
    projectId: string;
    projectName: string;
    projectColor?: string;
} | null;

let dialog: ProjectDialog = null;
const listeners = new Set<(next: ProjectDialog) => void>();

function setProjectDialog(next: ProjectDialog) {
    dialog = next;
    listeners.forEach((listen) => listen(next));
}

/** Rendered next to the sidebar (not inside the Sheet) so closing the sheet does not unmount the dialog. */
export function ProjectActionDialogs() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [state, setState] = useState(dialog);
    const { rename, remove, isRenameLoading, isDeleteLoading } = useProjectActions(state?.projectId ?? "");
    const [newTitle, setNewTitle] = useState("");
    const [selectedColor, setSelectedColor] = useState<string>(DEFAULT_PROJECT_COLOR);
    const { data: projects } = useProjects({
        pageSize: 50,
        page: 1,
        order: "desc",
        searchQuery: "",
    });
    const setSidebarProjectsList = useChatStore((s) => s.setSidebarProjectsList);

    useEffect(() => {
        listeners.add(setState);
        return () => { listeners.delete(setState); };
    }, []);

    useEffect(() => {
        if (state) {
            setNewTitle(state.projectName);
            setSelectedColor(state.projectColor || DEFAULT_PROJECT_COLOR);
        }
    }, [state]);

    useEffect(() => {
        if (!projects?.length) return;
        if (useChatStore.getState().sidebarProjectsList.length) return;
        setSidebarProjectsList(projects);
    }, [projects, setSidebarProjectsList]);

    const close = () => setProjectDialog(null);

    if (!state) return null;

    if (state.type === "delete") {
        return (
            <CustomDialog
                isOpen
                isLoading={isDeleteLoading}
                setIsOpen={(open) => { if (!open) close(); }}
                title={t("CONFIRM_ACTION")}
                onConfirm={async () => { await remove(); close(); }}
                confirmText={t("YES")}
                cancelText={t("CANCEL")}
                description=""
            >
                <p>{t("CONFIRM_ACTION_DESCRIPTION")}</p>
            </CustomDialog>
        );
    }

    return (
        <CustomDialog
            isOpen
            isLoading={isRenameLoading}
            setIsOpen={(open) => { if (!open) close(); }}
            title={t("RENAME")}
            onConfirm={async () => {
                if (!newTitle.trim()) return;
                await rename(newTitle, selectedColor);
                queryClient.invalidateQueries({ queryKey: ["getProject", state.projectId] });
                close();
            }}
            confirmText={t("SAVE")}
            cancelText={t("CANCEL")}
            isConfirmDisabled={newTitle.trim() === ""}
            description=""
        >
            <DebouncedInput
                key={state.projectId}
                placeholder={t("ENTER_NEW_TITLE")}
                initialValue={newTitle}
                onDebouncedChange={setNewTitle}
                className="h-10 w-full rounded-3xl border border-primary bg-background pr-8 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <div className="mt-4 flex w-full flex-col items-start gap-4">
                <label className="font-sans text-sm font-bold not-italic leading-[120%] text-grey-700 dark:text-true-white">
                    {t('COLOR') || 'Color'}
                </label>
                <ProjectColorSwatches selectedColor={selectedColor} onSelect={setSelectedColor} />
            </div>
        </CustomDialog>
    );
}

export interface ProjectActionsProps {
    projectId: string;
    projectName: string;
    projectColor?: string;
}

export const ProjectActions = ({ projectId, projectName, projectColor }: ProjectActionsProps) => {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'ml-1 rounded-md p-1 transition-opacity hover:bg-muted-foreground/10',
                        menuOpen
                            ? 'opacity-100'
                            : 'opacity-100 lg:pointer-events-none lg:opacity-0 lg:group-hover:pointer-events-auto lg:group-hover:opacity-100',
                    )}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Ellipsis
                        size={16}
                        strokeWidth={2.2}
                        className="text-sidebar-foreground dark:group-hover:!text-[#fff]"
                    />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                side="right"
                align="end"
                className="min-w-48 rounded-[8px]"
                onCloseAutoFocus={(e) => e.preventDefault()}
            >
                <DropdownMenuItem
                    onClick={(e) => e.stopPropagation()}
                    onSelect={() => {
                        window.setTimeout(() => {
                            setProjectDialog({ type: "rename", projectId, projectName, projectColor });
                        }, 300);
                    }}
                >
                    <ConversationMenuIcon optionId="rename" />
                    <span>{t("RENAME")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    className={dropdownMenuItemDestructiveClass}
                    onClick={(e) => e.stopPropagation()}
                    onSelect={() => {
                        window.setTimeout(() => {
                            setProjectDialog({ type: "delete", projectId, projectName, projectColor });
                        }, 300);
                    }}
                >
                    <ConversationMenuIcon optionId="delete" />
                    <span>{t("DELETE")}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
